import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import LoadingSpinner from './components/shared/UI/LoadingSpinner';
import ProtectedRoute from './components/shared/ProtectedRoute';
import { PORTAL_ROLES, hasRole, hasPortalAccess } from './utils/roleHelpers';
import AdminLayout from './components/shared/Layout/AdminLayout';
import CompanyLayout from './components/shared/Layout/CompanyLayout';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import FirstRunSetupPage from './pages/auth/FirstRunSetupPage';

// Admin pages
import FundsPage from './pages/admin/FundsPage';
import StreamsPage from './pages/admin/StreamsPage';
import CountriesPage from './pages/admin/CountriesPage';
import FXRatesPage from './pages/admin/FXRatesPage';
import UsersPage from './pages/admin/UsersPage';
import RubricsPage from './pages/admin/RubricsPage';
import DDQPage from './pages/admin/DDQPage';
import TermSheetTemplatesPage from './pages/admin/TermSheetTemplatesPage';
import ApprovalSequencesPage from './pages/admin/ApprovalSequencesPage';
import SLAPage from './pages/admin/SLAPage';
import InstrumentsPage from './pages/admin/InstrumentsPage';
import ICMembersPage from './pages/admin/ICMembersPage';

// Public PIF pages
import PIFFormPage from './pages/public/PIFFormPage';
import PIFReopenPage from './pages/public/PIFReopenPage';

// Portal pages
import DashboardPage from './pages/portal/DashboardPage';
import PipelinePage from './pages/portal/PipelinePage';
import AnalyticsPage from './pages/portal/AnalyticsPage';
import DealRecordPage from './pages/portal/DealRecordPage';
import AnalystReviewPage from './pages/portal/AnalystReviewPage';
import PeerReviewPage from './pages/portal/PeerReviewPage';
import HOPReviewPage from './pages/portal/HOPReviewPage';
import IICApprovalPage from './pages/portal/IICApprovalPage';

// Company portal pages
import CompanyLoginPage from './pages/company/CompanyLoginPage';
import CompanyDSQPage from './pages/company/CompanyDSQPage';

import './styles/admin.css';

function RootRedirect() {
  const { user, userProfile, loading, authReady } = useAuth();
  if (loading) return <LoadingSpinner fullPage />;
  if (!user) return <Navigate to="/login" replace />;
  // Authenticated, but the profile/claims are still resolving. Never pick a
  // destination from an unresolved profile — that is what sent roleless users
  // into the analyst shell. Checked after !user, because authReady is also
  // false when signed out.
  if (!authReady) return <LoadingSpinner fullPage />;
  if (hasRole(userProfile, 'company')) return <Navigate to="/company/dsq" replace />;
  if (hasRole(userProfile, 'admin')) return <Navigate to="/admin/funds" replace />;
  if (hasPortalAccess(userProfile)) return <Navigate to="/dashboard" replace />;
  // No recognised role — never default into the internal portal.
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes — no authentication required */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup" element={<FirstRunSetupPage />} />
        <Route path="/apply/:fundId" element={<PIFFormPage />} />
        <Route path="/apply/:fundId/:streamId" element={<PIFFormPage />} />
        <Route path="/apply/reopen/:token" element={<PIFReopenPage />} />

        {/* Company portal routes */}
        <Route path="/company/login" element={<CompanyLoginPage />} />
        <Route path="/company/dsq" element={
          <ProtectedRoute requiredRole={['company']}>
            <CompanyLayout>
              <CompanyDSQPage />
            </CompanyLayout>
          </ProtectedRoute>
        } />

        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Protected portal routes — PORTAL_ROLES keeps company and roleless
            users out of the internal shell. Defence in depth on top of the
            Firestore rules, which already deny them the data. */}
        <Route path="/dashboard" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><DashboardPage /></ProtectedRoute>} />
        <Route path="/pipeline" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><PipelinePage /></ProtectedRoute>} />
        <Route path="/analytics" element={
          <ProtectedRoute requiredRole={['analyst','head_of_pipeline','cio','md','admin']}>
            <AnalyticsPage />
          </ProtectedRoute>
        } />
        <Route path="/pipeline/:dealId" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><DealRecordPage /></ProtectedRoute>} />
        <Route path="/pipeline/:dealId/review" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><AnalystReviewPage /></ProtectedRoute>} />
        <Route path="/pipeline/:dealId/peer-review" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><PeerReviewPage /></ProtectedRoute>} />
        <Route path="/pipeline/:dealId/hop-review" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><HOPReviewPage /></ProtectedRoute>} />
        <Route path="/pipeline/:dealId/iic-approval" element={<ProtectedRoute requiredRole={PORTAL_ROLES}><IICApprovalPage /></ProtectedRoute>} />

        {/* Protected admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/funds" replace />} />
          <Route path="funds" element={<FundsPage />} />
          <Route path="streams" element={<StreamsPage />} />
          <Route path="countries" element={<CountriesPage />} />
          <Route path="fx-rates" element={<FXRatesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="rubrics" element={<RubricsPage />} />
          <Route path="ddq" element={<DDQPage />} />
          <Route path="term-sheet-templates" element={<TermSheetTemplatesPage />} />
          <Route path="approval-sequences" element={<ApprovalSequencesPage />} />
          <Route path="sla" element={<SLAPage />} />
          <Route path="instruments" element={<InstrumentsPage />} />
          <Route path="ic-members" element={<ICMembersPage />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
